import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Empty, ErrorBox, Row, Spinner } from '../components/ui.js';

export function AssetsPage(): JSX.Element {
  const { t } = useI18n();
  const [schemaId, setSchemaId] = useState<string | null>(null);
  const [aql, setAql] = useState('');
  const [runAql, setRunAql] = useState('');
  const [objectId, setObjectId] = useState<string | null>(null);

  const workspace = useQuery({
    queryKey: ['assets-workspace'],
    queryFn: () => api<{ workspaceId: string | null; available: boolean }>('/assets/workspace'),
  });

  const schemas = useQuery({
    queryKey: ['assets-schemas'],
    queryFn: () =>
      api<{ values: Array<{ id: string; name: string; objectCount?: number }> }>('/assets/schemas'),
    enabled: workspace.data?.available === true,
  });

  const objectTypes = useQuery({
    queryKey: ['assets-types', schemaId],
    queryFn: () =>
      api<{ objectTypes: Array<{ id: string; name: string }> }>(
        `/assets/schemas/${schemaId}/objecttypes`,
      ),
    enabled: schemaId !== null,
  });

  const results = useQuery({
    queryKey: ['assets-aql', runAql],
    queryFn: () =>
      api<{ values: Array<{ id: string; label: string; objectKey: string }> }>('/assets/aql', {
        method: 'POST',
        body: { qlQuery: runAql, maxResults: 50 },
      }),
    enabled: runAql.length > 0,
  });

  const detail = useQuery({
    queryKey: ['assets-object', objectId],
    queryFn: () =>
      api<{
        object: { id: string; label: string; objectKey: string };
        attributes: Array<{
          id: string;
          objectTypeAttribute?: { name: string };
          objectAttributeValues: Array<{ displayValue?: string; value?: string }>;
        }>;
      }>(`/assets/objects/${objectId}`),
    enabled: objectId !== null,
  });

  if (workspace.isLoading) return <Spinner />;
  if (workspace.error) {
    return (
      <div className="page">
        <ErrorBox error={workspace.error} />
      </div>
    );
  }
  if (!workspace.data?.available) {
    return (
      <div className="page">
        <Empty text={t('error.ProviderUnsupported')} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="header">
        <h1>{t('assets.title')}</h1>
      </div>

      <div className="scroll-x">
        {(schemas.data?.values ?? []).map((schema) => (
          <button
            key={schema.id}
            type="button"
            className={`chip ${schemaId === schema.id ? 'chip-indeterminate' : ''}`}
            onClick={() => setSchemaId(schema.id)}
          >
            {schema.name} ({schema.objectCount ?? 0})
          </button>
        ))}
      </div>

      {objectTypes.data ? (
        <Card title={t('assets.object_types')}>
          {objectTypes.data.objectTypes.slice(0, 30).map((type) => (
            <Row
              key={type.id}
              title={type.name}
              onClick={() => {
                const query = `objectType = "${type.name}"`;
                setAql(query);
                setRunAql(query);
              }}
            />
          ))}
        </Card>
      ) : null}

      <Card title="AQL">
        <div className="field">
          <textarea
            className="mono"
            value={aql}
            placeholder='objectType = "Host" AND Status = "Active"'
            onChange={(event) => setAql(event.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn-block"
          disabled={aql.trim().length === 0}
          onClick={() => setRunAql(aql)}
        >
          {t('common.search')}
        </button>
      </Card>

      {results.isLoading ? <Spinner /> : null}
      {results.error ? <ErrorBox error={results.error} /> : null}
      {results.data ? (
        <Card>
          {results.data.values.length === 0 ? <Empty /> : null}
          {results.data.values.map((object) => (
            <Row
              key={object.id}
              onClick={() => setObjectId(object.id)}
              title={object.label}
              subtitle={object.objectKey}
            />
          ))}
        </Card>
      ) : null}

      {detail.data ? (
        <Card title={detail.data.object.label}>
          {detail.data.attributes.map((attribute) => (
            <Row
              key={attribute.id}
              title={attribute.objectTypeAttribute?.name ?? attribute.id}
              right={attribute.objectAttributeValues
                .map((value) => value.displayValue ?? value.value ?? '')
                .join(', ')}
            />
          ))}
        </Card>
      ) : null}
    </div>
  );
}
